import {match} from 'ts-pattern';

import {findLastIndex, insert, isNil} from 'ramda';

import {UpdatePositionInSaleVehicleRequestBody} from '@dms/api/vehicle';

import {UnifiedAlbumImage} from '../types/UnifiedAlbumImage';

type PositionsWalk = {
  positions: UpdatePositionInSaleVehicleRequestBody[];
  previousSlot: number;
  minOwnPosition: number;
};

const getPosition = (image: UnifiedAlbumImage) => image.position ?? image.weight;

export const getOwnPromoPhotoPositions = (
  renderedImages: UnifiedAlbumImage[],
  droppedImages: UnifiedAlbumImage[]
) => {
  const targetImages = renderedImages
    .flatMap((image, index) => (image.isCampaignPhoto ? [{image, index}] : []))
    .reduce(
      (acc, {image, index}) => insert(index, image, acc),
      droppedImages.filter((image) => !image.isCampaignPhoto)
    );
  const lastRegularIndex = findLastIndex((image) => !image.isPromoPhoto, targetImages);
  const promoSlots = targetImages.flatMap((image, index) =>
    image.isPromoPhoto
      ? [{image, slot: index + 1, isAfterLastRegular: index > lastRegularIndex}]
      : []
  );

  const walk = promoSlots.reduce<PositionsWalk | null>(
    (acc, {image, slot, isAfterLastRegular}, index) => {
      if (isNil(acc)) {
        return null;
      }

      const nextCampaignPosition = Math.min(
        ...promoSlots
          .slice(index + 1)
          .filter((promoSlot) => promoSlot.image.isCampaignPhoto)
          .map((promoSlot) => getPosition(promoSlot.image))
      );
      const position = match({
        isCampaignPhoto: image.isCampaignPhoto,
        hasGap: slot > acc.previousSlot + 1,
      })
        .with({isCampaignPhoto: true}, () => getPosition(image))
        .with({hasGap: true}, () => slot)
        .otherwise(() => acc.minOwnPosition);
      const landingSlot = Math.max(acc.previousSlot + 1, position);
      const isLandingValid = isAfterLastRegular ? landingSlot >= slot : landingSlot === slot;

      if (!isLandingValid) {
        return null;
      }

      if (image.isCampaignPhoto) {
        return {
          ...acc,
          previousSlot: landingSlot,
          minOwnPosition: Math.max(acc.minOwnPosition, position),
        };
      }

      if (position < acc.minOwnPosition || position >= nextCampaignPosition) {
        return null;
      }

      return {
        positions: [...acc.positions, {saleVehiclePromoPhotoId: image.id, position}],
        previousSlot: landingSlot,
        minOwnPosition: position + 1,
      };
    },
    {positions: [], previousSlot: 0, minOwnPosition: 1}
  );

  if (isNil(walk)) {
    return null;
  }

  const hasPositionChange = walk.positions.some(({saleVehiclePromoPhotoId, position}) => {
    const image = targetImages.find((targetImage) => targetImage.id === saleVehiclePromoPhotoId);
    return isNil(image) || getPosition(image) !== position;
  });

  return hasPositionChange ? walk.positions : null;
};
